import _ from 'lodash';
import { RELATIVE_DATE_FILTERS } from '@/utils/scheduling';

// Column definitions for the ag-grid reports. `t` is a translation function, e.g. `key => this.$t(key)`.

function commonFilterParams () {
  return {
    buttons: ['apply', 'reset'],
    closeOnApply: true,
    filterPlaceholder: '',
    maxNumConditions: 1
  };
}

/**
 * A column without a filter.
 */
export function plainColumn (field, headerName, { sortable = false } = {}) {
  return {
    field,
    headerName,
    sortable
  };
}

/**
 * A column filtered by the text its values contain.
 */
export function textColumn (field, headerName) {
  return {
    field,
    headerName,
    filter: 'agTextColumnFilter',
    filterParams: {
      ...commonFilterParams(),
      defaultOption: 'contains',
      filterOptions: ['contains']
    },
    sortable: false
  };
}

/**
 * A sortable column filtered by date. Relative date options stay relative in saved templates; the report store turns
 * them into dates on every request.
 */
export function dateColumn (field, headerName, t) {
  return {
    field,
    headerName,
    filter: 'agDateColumnFilter',
    filterParams: {
      ...commonFilterParams(),
      browserDatePicker: true,
      defaultOption: 'inRange',
      filterOptions: [
        'equals',
        'inRange',
        ...RELATIVE_DATE_FILTERS.map((key) => ({
          displayKey: key,
          displayName: t(`labels.${key}`),
          numberOfInputs: 0,
          predicate: () => true // Required by ag-grid; rows are filtered by the server.
        }))
      ]
    },
    sortable: true
  };
}

/**
 * A column filtered by a set of values. Each value is an object with an `id`, which is sent to the server, and a
 * `label` property shown to the user.
 */
export function setColumn (field, headerName, values, { label = 'name', showTooltips = false, sortable = false, suppressMiniFilter = false } = {}) {
  // ag-grid can ask for a key or label without a value (e.g. for a key it no longer has), so don't assume one.
  const filterParams = {
    ...commonFilterParams(),
    keyCreator: params => _.get(params.value, 'id', null),
    suppressSorting: true,
    valueFormatter: params => _.get(params.value, label, null),
    values
  };
  if (showTooltips) {
    filterParams.showTooltips = true;
  }
  if (suppressMiniFilter) {
    filterParams.suppressMiniFilter = true;
  }
  return {
    field,
    headerName,
    filter: 'agSetColumnFilter',
    filterParams,
    sortable
  };
}

/**
 * A column of "Yes"/"No" values, filtered by either.
 */
export function yesNoColumn (field, headerName, t) {
  return setColumn(
    field,
    headerName,
    [{ id: 'false', name: t('labels.no') }, { id: 'true', name: t('labels.yes') }],
    { suppressMiniFilter: true }
  );
}

/**
 * Set filter values for records with a `name`, sorted by name.
 */
export function namedValues (records) {
  return _.sortBy(records.map(record => ({ id: record.id, name: record.name })), 'name');
}
